import React, { FC, Fragment, memo } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { List } from 'immutable';
import { isEqual } from 'lodash';
import { compose } from '@reduxjs/toolkit';

import type { BettingContextProps } from '../../types';
import type { ReduxState } from 'app/redux/types';

import { withinBettingContext } from 'app/contexts/betting';
import { Icon, IconSize } from 'app/design-system/components/Icon';
import { useTranslation } from 'app/lang';
import { isFFPrematchWS } from 'app/lib/site-configs/selectors';
import type { RecordOfSlotEntryType } from 'app/lib/SlotEntry';

import { getSlotContentComponent } from './components';

import dictionary from './SlotList.dictionary.json';

import styles from './SlotList.module.scss';

// ---

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyType = any;

type StoreProps = {
  [key: string]: AnyType;
  // TODO: write types, if you have some free time please :)
  stakeSlots: List<RecordOfSlotEntryType>;
};

type SlotListProps = {
  [key: string]: AnyType;
  // TODO: write types, if you have some free time please :)
  matchId: number;
  lineId: number;
  slotStakeDataGaEvent: string;
  // className: PropTypes.string,
  // isOutrightMatch: PropTypes.bool,
  // // этот пропс создан для временной страницы и используется как доп. класс и также как флаг по использованию нужного селектора
  // // после рейстайла страницы матча, удалить
  // slotClass: PropTypes.string,
  // isLock: PropTypes.bool,
};

const SlotList: FC<SlotListProps & StoreProps> = memo(({
  stakeSlots,
  className,
  slotClass,
  isOutrightMatch,
  isLock,
  slotStakeDataGaEvent,
}) => {
  const { lang, t } = useTranslation();

  if (stakeSlots?.size === 0) {
    return null;
  }

  const containerClassNames = classNames(styles.lineSlotContainer, className, {
    [styles.outrightMatch]: isOutrightMatch,
  });

  return (
    <div className={containerClassNames}>
      {stakeSlots?.map((slot) => {
        return (
          <Fragment key={slot.id}>
            <div className={classNames(styles.slotName, slotClass)}>
              {slot.getSlotName(lang)}

              {isLock && (
                <Icon className={styles.disabledIcon}
                      icon="lock"
                      title={t(dictionary.disabledTitle)}
                      size={IconSize.SMALL} />
              )}
            </div>

            <div className={classNames(styles.slotStakes, slotClass)}>
              {getSlotContentComponent(slot.family, slot.stakes, slotStakeDataGaEvent)}
            </div>
          </Fragment>
        );
      })}
    </div>
  );
}, (prevProps, nextProps) => {
  // todo: refactor with selectors, not `shouldComponentUpdate`
  return !(
    prevProps.isLock !== nextProps.isLock
    || prevProps.stakeSlots?.size !== nextProps.stakeSlots?.size
    || prevProps.stakeSlots.some(
      (slot, index: number) => !isEqual(slot, nextProps.stakeSlots.get(index)),
    )
  );
});

const mapStateToProps = (state: ReduxState, {
  lineId,
  matchId,
  slotClass,
  $betting: {
    content: {
      selectors: {
        matches,
      },
    },
    matchContent,
    grid: {
      selectors: {
        stakeSlotsSelector,
        stakeSlotsWithMainStakesSelector,
      },
    },
    matchGrid,
    staking,
  },
}: BettingContextProps & SlotListProps): StoreProps => {
  const isPrematchWS = isFFPrematchWS(state);
  const matchesWS = matchContent?.selectors?.matches;
  const stakeSlotsWSSelector = matchGrid?.selectors?.stakeSlotsSelector;
  const stakeSlotsWithMainStakesWSSelector = matchGrid?.selectors?.stakeSlotsWithMainStakesSelector;

  const match = (isPrematchWS && matchesWS)
    ? matchesWS(state).get(matchId)
    : matches(state).get(matchId);

  const conflictedIds = staking.selectors.selectAllConflictedIds(state);
  const isLock = !match?.isOpen && !conflictedIds.size;

  const stakeSlots = (isPrematchWS && stakeSlotsWSSelector)
    ? stakeSlotsWSSelector(state, lineId, matchId)
    : stakeSlotsSelector(state, lineId, matchId);

  const stakeSlotsWithMainStakes = (isPrematchWS && stakeSlotsWithMainStakesWSSelector)
    ? stakeSlotsWithMainStakesWSSelector(state, lineId, matchId)
    : stakeSlotsWithMainStakesSelector(state, lineId, matchId);

  return ({
    stakeSlots: slotClass
      ? stakeSlotsWithMainStakes
      : stakeSlots,
    isLock,
  });
};

export default compose<FC<SlotListProps>>(
  withinBettingContext,
  connect(mapStateToProps),
)(SlotList);
